import { FC, useEffect, useState } from 'react';

// API
import apibridge from '../../../apibridge';

// REDUX
import { ToastMessagesSlice } from '../../../store/slice/ToastMessages';
import { useDispatch } from 'react-redux';

// PACKAGES
import { Modal, Spinner } from 'react-bootstrap';

// COMPONENTS
import SvgMask from '../../../components/_Helpers/SvgMask';

// UTILS
import { guid } from '../../../libs/utils';

// TYPES
import {
	AssessmentMoveListAssessmentPeriod,
	AssessmentValidateMoveCommand,
	AssessmentMoveCommand,
	AssessmentSubType
} from '../../../api/models';

interface MoveAssessmentReadingModalProps {
	show: boolean;
	onHide: () => void;
	firstName?: string;
	lastName?: string;
	assessmentName: string;
	assessmentPeriodId?: string;
	studentId?: string;
	assessmentId?: string;
	type?: AssessmentSubType;
	onMoveSuccess: () => void;
}

const MoveAssessmentReadingModal: FC<MoveAssessmentReadingModalProps> = ({
	show,
	onHide,
	firstName,
	lastName,
	assessmentName,
	assessmentPeriodId,
	studentId,
	assessmentId,
	type,
	onMoveSuccess
}) => {
	const dispatch = useDispatch();

	const [isLoadingMoveAssessments, setIsLoadingMoveAssessments] = useState(false);
	const [isMovingAssessment, setIsMovingAssessment] = useState(false);

	const [assessmentPeriods, setAssessmentPeriods] = useState<AssessmentMoveListAssessmentPeriod[]>([]);

	const [isValidateSuccess, setIsValidateSuccess] = useState(false);
	const [isValidateWillOverwrite, setIsValidateWillOverwrite] = useState(false);

	const [selectedAssessmentPeriod, setSelectedAssessmentPeriod] = useState<AssessmentMoveListAssessmentPeriod | null>(
		null
	);

	const currentAssessmentPeriod = assessmentPeriods.find(
		(assessmentPeriod) => assessmentPeriod.key === assessmentPeriodId
	);

	// Get Assessment Periods List
	const getAssessmentMoveListData = async () => {
		setIsLoadingMoveAssessments(true);

		const response = await apibridge.getAssessmentMoveList();

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setAssessmentPeriods(response.data.result.assessmentPeriods || []);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Assessment Move List error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoadingMoveAssessments(false);
	};

	useEffect(() => {
		if (show) {
			getAssessmentMoveListData();
		}
	}, [show]); // eslint-disable-line react-hooks/exhaustive-deps

	// Validate before Move
	const validateAssessmentMove = async () => {
		if (!selectedAssessmentPeriod || !studentId || !assessmentId || !assessmentPeriodId || !type) {
			console.error('Missing required parameters for validation');
			return;
		}

		const validateData: AssessmentValidateMoveCommand = {
			studentIds: [studentId],
			currentAssessmentPeriodId: assessmentPeriodId,
			newAssessmentPeriodId: selectedAssessmentPeriod.key,
			type: type,
			assessmentId: assessmentId
		};

		const response = await apibridge.postAssessmentValidateMove(validateData);

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setIsValidateSuccess(response.data.result.success || false);

				setIsValidateWillOverwrite(response.data.result.willOverwrite || false);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Assessment Validate Move error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}
	};

	useEffect(() => {
		if (selectedAssessmentPeriod) {
			validateAssessmentMove();
		}
	}, [selectedAssessmentPeriod]); // eslint-disable-line react-hooks/exhaustive-deps

	// Move Assessment
	const moveAssessment = async () => {
		if (!selectedAssessmentPeriod || !studentId || !assessmentId || !assessmentPeriodId || !type) {
			console.error('Missing required parameters for moving assessment');
			return;
		}

		setIsMovingAssessment(true);

		const moveData: AssessmentMoveCommand = {
			studentIds: [studentId],
			currentAssessmentPeriodId: assessmentPeriodId,
			newAssessmentPeriodId: selectedAssessmentPeriod.key,
			type: type,
			assessmentId: assessmentId
		};

		const response = await apibridge.postAssessmentMove(moveData);

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				// Save the selected period label before resetting state
				const movedToLabel = selectedAssessmentPeriod?.label;

				// Close modal and show success message
				handleClose();

				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'success',
						heading: 'Assessment moved successfully',
						description: `Assessment record has been moved to ${movedToLabel} and the previously viewed assessment period has been reset.`
					})
				);

				// Call parent callback to refresh data
				onMoveSuccess();
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Assessment Move error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsMovingAssessment(false);
	};

	// Handle modal close - reset state
	const handleClose = () => {
		setSelectedAssessmentPeriod(null);
		setIsValidateSuccess(false);
		setIsValidateWillOverwrite(false);
		onHide();
	};

	return (
		<Modal show={show} onHide={handleClose} centered>
			<Modal.Body className="d-flex flex-column">
				<h2 className="mb-3 text-shades-800">
					<strong>Move assessment record</strong>
				</h2>

				<p className="text-dark-text body-normal mb-4">
					Select which assessment period you want to move the{' '}
					<strong>{assessmentName === 'GPK' ? 'Grapheme-phoneme Knowledge' : 'Reading (Non-words)'}</strong> record for{' '}
					<strong>
						{firstName} {lastName}
					</strong>{' '}
					.
				</p>

				<div className="row mb-2">
					<div className="col-3">
						<div className="d-flex align-items-center justify-content-end h-100">
							<label className="body-normal fw-bold text-dark-text text-end">Current period</label>
						</div>
					</div>

					<div className="col-9">
						<input type="text" className="form-control" disabled value={currentAssessmentPeriod?.label || ''} />
					</div>
				</div>

				<div className={`row ${isValidateWillOverwrite ? 'mb-0' : 'mb-4'}`}>
					<div className="col-3">
						<div className="d-flex align-items-center justify-content-end h-100">
							<label htmlFor="assessmentPeriod" className="body-normal fw-bold text-dark-text">
								Move to
							</label>
						</div>
					</div>

					<div className="col-9">
						<select
							value={selectedAssessmentPeriod?.key || ''}
							onChange={(e) => {
								const selectedKey = e.target.value;
								const selectedPeriod = assessmentPeriods.find((period) => period.key === selectedKey);
								setSelectedAssessmentPeriod(selectedPeriod || null);
							}}
							className="form-select"
							name="assessmentPeriod"
							id="assessmentPeriod"
							disabled={isLoadingMoveAssessments}
						>
							<option value="" disabled>
								Please select an assessment period
							</option>

							{assessmentPeriods
								?.filter((assessmentPeriod) => assessmentPeriod.key !== currentAssessmentPeriod?.key)
								?.map((assessmentPeriod) => {
									return (
										<option key={assessmentPeriod.key} value={assessmentPeriod.key}>
											{assessmentPeriod.label}
										</option>
									);
								})}
						</select>
					</div>
				</div>

				{isValidateWillOverwrite && (
					<div className="move-assessment-error-box">
						<SvgMask path={'/svg/caution-outline.svg'} width={20} height={20} />

						<div>
							<p className="mb-2 text-start">
								There is existing assessment data in the assessment period you are moving the current assessment record
								to.
							</p>

							<p className="mb-0 text-start">
								If you proceed, the current assessment record in <strong>{currentAssessmentPeriod?.label}</strong> will
								overwrite the records in <strong>{selectedAssessmentPeriod?.label}</strong>.
							</p>
						</div>
					</div>
				)}

				<div className="button-group d-flex gap-3 mt-0">
					<button
						type="button"
						onClick={handleClose}
						className="btn btn-white btn-lg w-100"
						disabled={isLoadingMoveAssessments}
					>
						Cancel
					</button>

					<button
						type="button"
						onClick={moveAssessment}
						className="btn btn-lg w-100"
						disabled={isLoadingMoveAssessments || !isValidateSuccess}
					>
						{isMovingAssessment ? (
							<Spinner animation="border" role="status" size="sm">
								<span className="visually-hidden">Loading...</span>
							</Spinner>
						) : (
							<> Move record</>
						)}
					</button>
				</div>
			</Modal.Body>
		</Modal>
	);
};

export default MoveAssessmentReadingModal;
